import React, { FC } from 'react';
import { Banner, Text, BulletList, theme } from '@blueprint/ui';
import styled from '@emotion/styled';
import Link from 'next/link';

import { getTranslator } from '../../shared/i18n/getTranslator';
import { SearchPageType } from '../SearchContext';
import searchTranslations from 'public/locales/en/search.json';

const StyledLink = styled('a')`
    line-height: ${theme.type.lineHeight};
    color: ${theme.colors['primary-brand']};
    text-decoration: none;
    font-weight: bold;
`;

const t = getTranslator(searchTranslations);
const noMatchIconTileTn = t('messages.noAreaMatch.iconTitle');
const noMatchTextTn = t('messages.noAreaMatch.text');
const noAreaMatchSuggestionsTn = t('messages.noAreaMatch.suggestions');
const noAreaMatchCountyNamesTn = t('messages.noAreaMatch.countyNames.text');
const noAreaMatchCitiesTn = t('messages.noAreaMatch.cities.text');
const noAreaMatchLondonSuburbsTn = t('messages.noAreaMatch.londonSuburbs.text');
const noAreaMatchPostcodesTn = t('messages.noAreaMatch.postCodes.text');
const noAreaMatchContactUsTn = t('messages.noAreaMatch.contactus.text');
const noAreaMatchContactUsLinkTn = t(
    'messages.noAreaMatch.contactus.links.link1.link'
);
const noAreaMatchContactUsLinkTextTn = t(
    'messages.noAreaMatch.contactus.links.link1.text'
);

interface Props {
    areaName?: string;
    searchType: SearchPageType;
}

export const ErrorArea: FC<Props> = ({ areaName = '', searchType }) => {
    const mapLinkToTranslation = (translationKey: string) => (
        key: string,
        index: number
    ) => (
        <Link
            key={`messages.noAreaMatch.${translationKey}.links.${key}`}
            href={t(
                `messages.noAreaMatch.${translationKey}.links.${key}.link`,
                {
                    searchType
                }
            )}
            passHref
        >
            <StyledLink>
                {index === 0 ? ' ' : ', '}
                {t(`messages.noAreaMatch.${translationKey}.links.${key}.text`)}
            </StyledLink>
        </Link>
    );
    return (
        <Banner
            data-testid="area-error-banner"
            strapline={noMatchIconTileTn}
            title={t('messages.noAreaMatch.title', { areaName })}
            text={noMatchTextTn}
        >
            <Text isBold>{noAreaMatchSuggestionsTn}</Text>
            <BulletList>
                <li>
                    {noAreaMatchCountyNamesTn}
                    {['link1', 'link2', 'link3'].map(
                        mapLinkToTranslation('countyNames')
                    )}
                </li>
                <li>
                    {noAreaMatchCitiesTn}
                    {['link1', 'link2', 'link3'].map(
                        mapLinkToTranslation('cities')
                    )}
                </li>
                <li>
                    {noAreaMatchLondonSuburbsTn}
                    {['link1', 'link2'].map(
                        mapLinkToTranslation('londonSuburbs')
                    )}
                </li>
                <li>
                    {noAreaMatchPostcodesTn}
                    {['link1', 'link2'].map(mapLinkToTranslation('postCodes'))}
                </li>
            </BulletList>
            <Text>
                {noAreaMatchContactUsTn}
                <StyledLink href={noAreaMatchContactUsLinkTn}>
                    {' '}
                    {noAreaMatchContactUsLinkTextTn}
                </StyledLink>
                .
            </Text>
        </Banner>
    );
};
